<template>
    <div id="app">
        <ModalStackComponent ref="modalStack" :root="root" />
        <div v-if="showApiBadge" class="api-env-badge">
            {{ apiBadgeText }}
        </div>
    </div>
</template>

<script lang="ts">
import { App as CapApp } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from "@capacitor/push-notifications"
import { AuthenticatedView, PromiseView } from "@restofrit/containers";
import { load, switchLanguage } from '@restofrit/i18n';
import { getCurrentEnvironment, Session } from "@restofrit/networking";
import { Customer, CustomerStatus, DeviceToken, DeviceTokenType, NotificationPreference, SessionEnvironment } from "@restofrit/structures";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { ComponentWithProperties, HistoryManager, ModalStackComponent, NavigationController } from "@simonbackx/vue-app-navigation";
import { Component, Vue } from "vue-property-decorator";

import { clearOSBadge, markNotificationsSeen } from './helpers/NotificationBadge';
import ChooseWholesalerView from "./views/create-customer/ChooseWholesalerView.vue";
import PromotionsView from "./views/promotions/PromotionsView.vue";
import TabBarController from './views/promotions/TabBarController.vue';
import { TabBarItem } from './views/promotions/TabBarItem';
import SettingsView from './views/settings/SettingsView.vue';
import WaitingForVerificationView from "./views/welcome/WaitingForVerificationView.vue";
import WelcomeView from "./views/welcome/WelcomeView.vue";

@Component({
    components: {
        ModalStackComponent
    },
})
export default class App extends Vue {
    root = this.getComponent()

    // Safety indicator for the environment switcher: whenever the SERVER says
    // this session is routed to test (see EnvironmentProxyMiddleware on the
    // backend — this is never decided client-side), show a persistent badge
    // so a tester can never forget they're off-live, and so you can visually
    // confirm a build is on live before promoting it to the store. Fetched
    // once per boot in buildComponent(); defaults to Production until then.
    //
    // TEMPORARY — hardcoded test build (see CLAUDE.md "Hardcoded test build").
    // `yarn build:test` points API_URL straight at the Azure test API and
    // disables the in-app switcher. In that build the server-driven value is
    // useless for the badge: the test deployment issues its own tokens, whose
    // `environment` column defaults to 'production', and nothing proxies
    // anywhere — so it would report Production while actually being on test.
    // Force the badge on instead. Remove this whole block, and restore the
    // switcher in SettingsView, when the switcher build ships.
    currentEnvironment: SessionEnvironment = process.env.TEST_BUILD === "true"
        ? SessionEnvironment.Test
        : SessionEnvironment.Production

    get showApiBadge(): boolean {
        return this.currentEnvironment !== SessionEnvironment.Production
    }

    get apiBadgeText(): string {
        return "⚠ TEST ENVIRONMENT"
    }

    activeCustomer: Customer | null = null

    pendingRegistrationToken: string | null = null

    appStateListener: PluginListenerHandle | null = null

    getComponent() {
        return new ComponentWithProperties(PromiseView, {
            promise: this.buildComponent.bind(this)
        })
    }

    async buildComponent() {
        await load()
        return new ComponentWithProperties(AuthenticatedView, {
            root: new ComponentWithProperties(PromiseView, {
                promise: async () => {
                    // Purely for the badge — routing itself is decided server-side
                    // on every request regardless of what this returns.
                    //
                    // TEMPORARY: skipped in a hardcoded test build, where the
                    // badge is forced on above and this call would overwrite it
                    // with Production. Restore unconditionally with the switcher.
                    if (process.env.TEST_BUILD !== "true") {
                        getCurrentEnvironment()
                            .then((environment) => { this.currentEnvironment = environment })
                            .catch((e) => console.error("Failed to fetch current environment", e))
                    }

                    const response = await Session.shared.authenticatedServer.request({
                        method: "GET",
                        path: "/customers/customers",
                        decoder: new ArrayDecoder(Customer as Decoder<Customer>)
                    })
                    const customers = response.data
                    if (customers.length == 0) {
                        // Onboarding flow
                        return new ComponentWithProperties(NavigationController, { 
                            root: new ComponentWithProperties(ChooseWholesalerView, {
                                callback: () => {
                                    // Reload
                                    (this.$refs.modalStack as ModalStackComponent).replace(this.getComponent(), false);
                                }
                            })
                        })
                    }

                    const customerNumber = customers.length;
                    const customer = customers[0]
                    this.activeCustomer = customer
                    console.log("User:", Session.shared.user?.email );
                    console.log("ActiveCustomer:", this.activeCustomer?.settings.businessName );

                    switchLanguage(customer.settings.language, false).catch(console.error)

                    // We need to send notification token again if 
                    // we already registered in the same flow
                    this.sendNotificationTokenIfNeeded().catch(console.error)

                    // Add live reload to this customer.
                    // buildComponent can run multiple times (onboarding callback, appUrlOpen),
                    // so remove the previous listener to avoid stacking duplicates
                    if (this.appStateListener) {
                        this.appStateListener.remove().catch(console.error)
                        this.appStateListener = null
                    }
                    this.appStateListener = await CapApp.addListener('appStateChange', ({ isActive }) => {
                        if (this.activeCustomer !== customer) {
                            // Ignore because a new customer is already fetched
                            return
                        }
                        console.log('App state changed. Is active?', isActive);
                        if (isActive) {
                            // Refetch the customers
                            this.reloadCustomer(customer).catch(e => {
                                console.error(e)
                            })
                            // Reset the OS badge baseline only — read/unread state is
                            // cleared when the notification center itself is opened
                            markNotificationsSeen(customer.id).catch(console.error)
                            clearOSBadge().catch(console.error)
                        }
                    });

                    // Also reset the badge baseline on cold start — appStateChange only fires on resume
                    markNotificationsSeen(customer.id).catch(console.error)
                    clearOSBadge().catch(console.error)

                    // Ask permissions for push notifications
                    if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
                        const permissions = await PushNotifications.checkPermissions()

                        // Per-type preferences take precedence over the legacy on/off flag
                        const prefs = customer.settings.notificationPreferences
                        const wantsNotifications = prefs
                            ? (prefs.promotion !== NotificationPreference.Off || prefs.reminder !== NotificationPreference.Off || prefs.account !== NotificationPreference.Off)
                            : customer.settings.notificationsEnabled

                        if (permissions.receive !== "denied" && permissions.receive !== "granted" && wantsNotifications) {
                            // Ask for permissions
                            await PushNotifications.requestPermissions()
                        }
                        PushNotifications.register().catch(console.error)
                    }

                    // Check status
                    if (customer.settings.status !== CustomerStatus.Accepted) {
                        return new ComponentWithProperties(ModalStackComponent, {
                            root: new ComponentWithProperties(NavigationController, { 
                                root: new ComponentWithProperties(WaitingForVerificationView, {
                                    customer,
                                    customerNumber
                                })
                            })
                        })
                    }

                    // Todo: pass customer along

                    return new ComponentWithProperties(ModalStackComponent, {
                        root: new ComponentWithProperties(TabBarController, {
                            items: [
                                new TabBarItem("promotions.title", "promotions", new ComponentWithProperties(NavigationController, { 
                                    root: new ComponentWithProperties(PromotionsView, {
                                        customer,
                                        customerNumber
                                    })
                                })),
                                new TabBarItem("settings.title", "settings", new ComponentWithProperties(NavigationController, { 
                                    root: new ComponentWithProperties(SettingsView, {
                                        customer,
                                        customerNumber
                                    })
                                }))
                            ]
                        })
                    })
                }
            }),
            loginRoot: new ComponentWithProperties(NavigationController, { 
                root: new ComponentWithProperties(WelcomeView, {})
            })
        });
    }

    async reloadCustomer(customer: Customer) {
        console.log("focused!")

        const response = await Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/customers/customers",
            decoder: new ArrayDecoder(Customer as Decoder<Customer>)
        })

        const customers = response.data
        const c = customers.find(cc => cc.id === customer.id)
        if (c) {
            customer.set(c)
            console.log("updated customer")
        }
    }

    async sendNotificationTokenIfNeeded() {
        if (!this.pendingRegistrationToken) {
            return
        }
        const customer = this.activeCustomer
        if (!customer) {
            console.log("Received push notifications token, but without an active customer")
            return
        }
        // Sending the token to the server...
        await Session.shared.authenticatedServer.request({
            method: "POST",
            path:  "/customers/"+customer.id+"/register-device",
            body: DeviceToken.create({
                id: this.pendingRegistrationToken,
                type: Capacitor.getPlatform() === 'ios' ? DeviceTokenType.iOS : DeviceTokenType.Android
            })
        })
        this.pendingRegistrationToken = null
    }

    mounted() {
        HistoryManager.activate();

        if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
            CapApp.addListener('appUrlOpen', (data) => {
                console.log("Did open app with url "+data.url)
                // Example url: https://beerswift.app/tabs/tabs2
                // slug = tabs/tabs2
                const url = new URL(data.url);
                const slug = url.pathname + url.search;
                console.log(slug)

                // We only push to the route if there is a slug present
                if (slug.length > 0) {
                    // Reload the view completely
                    HistoryManager.setUrl(slug);
                    (this.$refs.modalStack as ModalStackComponent).replace(this.getComponent(), false);
                }
            }).catch(console.error);

            PushNotifications.addListener("registration", (token) => {
                console.log("Received push notifications token, "+token.value)
                this.pendingRegistrationToken = token.value
                this.sendNotificationTokenIfNeeded().catch(console.error)
            }).catch(console.error)

            PushNotifications.addListener("registrationError", (err) => {
                console.error("Registration for push notifications failed with error")
                console.error(err)
            }).catch(console.error)

            if (Capacitor.getPlatform() === 'android') {
                // The old 'promotions_loud' channel passed sound: 'default', which the
                // Capacitor plugin resolves to a raw resource file named "default"
                // (res/raw/default) — that file doesn't exist, so the channel had an
                // invalid sound URI and played no sound (only vibration). Channels are
                // immutable once created, so we delete the broken one and create a new
                // id that omits `sound` entirely, which makes Android use the system
                // default notification sound.
                PushNotifications.deleteChannel({ id: 'promotions_loud' }).catch(console.error)

                PushNotifications.createChannel({
                    id: 'promotions_loud_v2',
                    name: 'Promotions',
                    importance: 5,
                    vibration: true,
                    visibility: 1
                }).catch(console.error)

                // The in-app Silent preference was removed (muting is handled via the
                // OS channel settings), so the backend only targets promotions_loud_v2
                // now. Clean up the orphaned silent channel on devices that created it.
                PushNotifications.deleteChannel({ id: 'promotions_silent' }).catch(console.error)
            }

            CapApp.addListener('backButton', () => {
                // This fixes the Android behaviour for the back button that won't close the app
                // We don't use history.length since it appears to be bugged in capacitor
                if (HistoryManager.counter > 0) {
                    window.history.back()
                } else {
                    CapApp.exitApp().catch(console.error)
                }
            }).catch(console.error);
        }
    }
}
</script>

<style lang="scss">
// We need to include the component styling of vue-app-navigation first
@use "~@restofrit/scss/main";
@import "~@simonbackx/vue-app-navigation/dist/main.css";

// Disable all user text selection in the user app
html {
    -webkit-touch-callout:none;
    user-select: none;
}

// Persistent "off-live" indicator for the runtime API switcher. Fixed to the
// bottom so it doesn't collide with the status bar / navigation bar, ignores
// pointer events so it never blocks the UI underneath, and sits above
// everything so it's always visible.
.api-env-badge {
    position: fixed;
    left: 50%;
    bottom: calc(8px + var(--rf-safe-area-bottom, 0px));
    transform: translateX(-50%);
    z-index: 2147483647;
    pointer-events: none;
    padding: 4px 12px;
    border-radius: 999px;
    background: #b00020;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}
</style>
